/**
 * Medical Tab Component for Client Detail Page
 * Displays diagnoses, conditions, physician info, DME, and MD Orders
 */
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Send, Eye, Edit, Link, Download, RotateCw, AlertTriangle, Users, Package } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function MedicalTab({
  client,
  mdOrders,
  onSendFaxOrder,
  onCreateDmeOrder,
  onViewMdOrder,
  onEditMdOrder,
  onSendSignLink,
  onFaxOrder,
  onQuickRefax,
  onDownloadPdf,
  onRefresh,
}) {
  const [pullingFromDirectory, setPullingFromDirectory] = useState(false);

  // Highlight what's missing so agencies can see at a glance what still
  // needs to be filled in after a slim online referral.
  const missing = useMemo(() => {
    const fields = [];
    if (!client.primary_diagnosis) fields.push("Primary Diagnosis");
    if (!client.care_reasons) fields.push("Reason for Home Care");
    if (!client.physician?.name) fields.push("Physician Name");
    if (!client.physician?.phone) fields.push("Physician Phone");
    if (!client.physician?.fax) fields.push("Physician Fax");
    if (!client.allergies) fields.push("Allergies");
    if (!client.dme_current) fields.push("Current DME");
    return fields;
  }, [client]);

  const handlePullFromPhysicianDirectory = async () => {
    setPullingFromDirectory(true);
    try {
      // Find a matching physician in the agency directory
      let match = null;
      if (client.physician?.id) {
        const res = await axios.get(`${API}/physicians/${client.physician.id}`);
        match = res.data;
      } else if (client.physician?.name) {
        const res = await axios.get(`${API}/physicians`);
        const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
        const needle = client.physician.name.trim().toLowerCase();
        match =
          list.find((p) => (p.name || "").trim().toLowerCase() === needle) ||
          list.find((p) => (p.name || "").toLowerCase().includes(needle));
      }
      if (!match) {
        toast.error(
          "No matching entry in the Physician Directory. Add the physician to the directory first."
        );
        return;
      }
      // Merge directory data into the client physician block — don't overwrite
      // fields the client already has, only fill the blanks.
      const merged = {
        id: match.id || client.physician?.id,
        name: client.physician?.name || match.name || "",
        clinic_name: client.physician?.clinic_name || match.clinic_name || match.practice_name || "",
        phone: client.physician?.phone || match.phone || "",
        fax: client.physician?.fax || match.fax || "",
        address: client.physician?.address
          || [match.address, [match.city, match.state].filter(Boolean).join(", "), match.zip_code].filter(Boolean).join(", "),
      };
      await axios.patch(`${API}/clients/${client.id}`, { physician: merged });
      toast.success("Filled physician info from directory");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't pull physician info");
    } finally {
      setPullingFromDirectory(false);
    }
  };

  return (
    <div data-testid="client-medical-tab">
      {missing.length > 0 && (
        <div
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4"
          data-testid="medical-missing-info-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                Missing medical information
              </p>
              <p className="text-xs text-amber-800 mt-1">
                This client came in through the online referral — some fields are
                still blank. Fill them in manually, or pull the physician block
                from your directory below.
              </p>
              <ul className="text-xs text-amber-900 mt-2 list-disc list-inside space-y-0.5">
                {missing.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
            <RouterLink to={`/clients/${client.id}/edit`}>
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                data-testid="fill-missing-info-btn"
              >
                <Edit className="w-4 h-4 mr-1" />
                Fill In
              </Button>
            </RouterLink>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mb-4">
        {(client.physician?.name || client.physician?.id) && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={handlePullFromPhysicianDirectory}
            disabled={pullingFromDirectory}
            data-testid="pull-from-physician-directory-btn"
            title="Fill blank physician fields from your Physician Directory entry"
          >
            <Users className="w-4 h-4" />
            {pullingFromDirectory ? "Pulling…" : "Pull From Physician Directory"}
          </Button>
        )}
        <RouterLink to={`/clients/${client.id}/edit`}>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="edit-medical-btn"
          >
            <Edit className="w-4 h-4" />
            Edit Medical Info
          </Button>
        </RouterLink>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Diagnoses */}
        <div className="form-section">
          <h3 className="form-section-title">Diagnoses</h3>
          <div className="space-y-4">
            <div className="info-item">
              <p className="info-label">Primary Diagnosis</p>
              <p className="info-value">{client.primary_diagnosis || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Secondary Diagnoses</p>
              <p className="info-value">
                {client.secondary_diagnoses?.length > 0
                  ? client.secondary_diagnoses.join(", ")
                  : "None"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Reason for Home Care</p>
              <p className="info-value">{client.care_reasons || "Not provided"}</p>
            </div>
          </div>
        </div>

        {/* Medical Conditions */}
        <div className="form-section">
          <h3 className="form-section-title">Medical Conditions</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Diabetic</span>
              <span
                className={`badge ${
                  client.is_diabetic ? "badge-active" : "badge-inactive"
                }`}
              >
                {client.is_diabetic ? "Yes" : "No"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Seizure History</span>
              <span
                className={`badge ${
                  client.has_seizure_history ? "badge-active" : "badge-inactive"
                }`}
              >
                {client.has_seizure_history ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>

        {/* Physician Information */}
        <div className="form-section">
          <h3 className="form-section-title">Physician Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <p className="info-label">Physician Name</p>
              <p className="info-value">{client.physician?.name || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Clinic</p>
              <p className="info-value">
                {client.physician?.clinic_name || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Phone</p>
              <p className="info-value">{client.physician?.phone || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Fax</p>
              <p className="info-value">{client.physician?.fax || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Last MD Visit</p>
              <p className="info-value">{client.last_md_visit || "Not provided"}</p>
            </div>
          </div>
        </div>

        {/* DME */}
        <div className="form-section">
          <div className="flex items-center justify-between mb-4 gap-2">
            <h3 className="form-section-title !mb-0">DME Supplies</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={onCreateDmeOrder}
              className="gap-1.5"
              title={client.dme_needed ? "Pre-fills a DME order with the DME Needed list" : "Create a DME order for the physician"}
              data-testid="create-dme-order-btn"
            >
              <Package className="w-4 h-4" />
              Create DME Order
            </Button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="info-item">
                <p className="info-label">Height</p>
                <p className="info-value">{client.height || "Not recorded"}</p>
              </div>
              <div className="info-item">
                <p className="info-label">Weight</p>
                <p className="info-value">{client.weight || "Not recorded"}</p>
              </div>
            </div>
            <div className="info-item">
              <p className="info-label">Current DME</p>
              <p className="info-value">{client.dme_current || "None"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">DME Needed</p>
              <p className="info-value">{client.dme_needed || "None"}</p>
            </div>
          </div>
        </div>

        {/* Fax MD Order */}
        <div className="form-section md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="form-section-title">Fax MD Order</h3>
            <Button
              size="sm"
              onClick={onSendFaxOrder}
              className="gap-2"
              data-testid="fax-md-order-btn"
            >
              <Send className="w-4 h-4" />
              Send Fax Order
            </Button>
          </div>
          
          {mdOrders.length === 0 ? (
            <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
              <Send className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No MD orders sent yet</p>
              <p className="text-xs mt-1">Click &quot;Send Fax Order&quot; to request physician orders</p>
            </div>
          ) : (
            <div className="space-y-3">
              {mdOrders.map((order) => (
                <div
                  key={order.id}
                  className="p-4 border border-slate-200 rounded-lg bg-slate-50"
                  data-testid={`md-order-card-${order.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-800">
                        {order.order_types.map(t => 
                          t === 'personal_care' 
                            ? 'Personal Care Service Evaluation and Treatment' 
                            : t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                        ).join(', ')}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Created: {order.created_at?.split('T')[0] || 'N/A'}
                      </p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      order.status === 'completed' 
                        ? 'bg-emerald-100 text-emerald-700'
                        : order.status === 'pending'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {order.status === 'completed' ? 'Signed' : order.status?.replace(/_/g, ' ') || 'Pending'}
                    </span>
                  </div>
                  {order.notes && (
                    <p className="text-sm text-slate-600 mt-2">{order.notes}</p>
                  )}
                  
                  {/* Signature Status */}
                  {order.physician_signature && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <p className="text-xs text-emerald-600 font-medium">
                        ✓ Signed by {order.physician_signer_name} on {order.physician_signature_date?.split('T')[0]}
                      </p>
                      {/* Display signature image if it's a base64 image */}
                      {order.physician_signature.startsWith('data:image') && (
                        <div className="mt-2 p-2 bg-emerald-50 rounded">
                          <img 
                            src={order.physician_signature} 
                            alt="Physician Signature" 
                            className="max-h-12 object-contain"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => onViewMdOrder(order)}
                        data-testid={`view-md-order-${order.id}`}
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </Button>
                      {!order.physician_signature && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => onEditMdOrder(order)}
                          data-testid={`edit-md-order-${order.id}`}
                        >
                          <Edit className="w-3 h-3" />
                          Edit
                        </Button>
                      )}
                      {!order.physician_signature && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => onSendSignLink("md_order", order.id)}
                          data-testid={`sign-link-md-order-${order.id}`}
                        >
                          <Link className="w-3 h-3" />
                          Send Sign Link
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => onFaxOrder("md_order", order.id)}
                        data-testid={`fax-md-order-${order.id}`}
                      >
                        <Send className="w-3 h-3" />
                        Send Fax
                      </Button>
                      {onQuickRefax && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1 border-teal-200 text-teal-700 hover:bg-teal-50"
                          onClick={() => onQuickRefax("md_order", order.id)}
                          data-testid={`refax-md-order-${order.id}`}
                          title="Re-fax to the last recipient (one click)"
                        >
                          <RotateCw className="w-3 h-3" />
                          Re-fax
                        </Button>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={() => onDownloadPdf(order.id)}
                      data-testid={`download-md-order-${order.id}`}
                    >
                      <Download className="w-3 h-3" />
                      PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
